import { Button } from "@faire/slate/components/button";
import { Core, Message } from "@faire/slate/foundation/color";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import React from "react";

import { INLINE_SPE_COPY } from "./inlineSpeCopy";
import { MockAiSparkleIcon } from "./MockAiSparkleIcon";

export type AiFieldStatus = "idle" | "pending" | "ready" | "error";

/**
 * Mirrors `SpeWizardModal/__internal__/AiAssistedField.tsx` for inline SPE fields.
 */
export const MockAiAssistedField: React.FC<{
  aiEnabled: boolean;
  status: AiFieldStatus;
  label: string;
  onRegenerate?: () => void;
  onRetry?: () => void;
  children: React.ReactNode;
}> = ({ aiEnabled, status, label, onRegenerate, onRetry, children }) => {
  if (!aiEnabled) {
    return <Column>{children}</Column>;
  }

  return (
    <Column>
      <Row align="center" justify="space-between">
        <Row align="center" gap="2x">
          <Typography variant="labelSansMedium" color={Core.text.primary}>
            {label}
          </Typography>
          {status === "ready" ? (
            <MockAiSparkleIcon tooltipText={INLINE_SPE_COPY.aiFieldTooltip} />
          ) : null}
        </Row>
        {onRegenerate ? (
          <Button
            size="small"
            variant="secondary"
            onClick={onRegenerate}
            loading={status === "pending"}
            disabled={status === "pending"}
          >
            {INLINE_SPE_COPY.regenerate}
          </Button>
        ) : null}
      </Row>
      <Spacer size="2x" />
      {children}
      {status === "error" ? (
        <Row align="center" justify="space-between" className="mt-2">
          <Typography variant="labelSansRegular" color={Message.text.critical}>
            {INLINE_SPE_COPY.aiError}
          </Typography>
          {onRetry ? (
            <Button size="small" variant="tertiary" onClick={onRetry}>
              {INLINE_SPE_COPY.retry}
            </Button>
          ) : null}
        </Row>
      ) : null}
    </Column>
  );
};
